
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface AppSetting {
  id: string;
  key: string;
  value: string;
  description?: string;
}

export const useAppSettings = (settingKey?: string) => {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<AppSetting[]>([]);
  const [currentSetting, setCurrentSetting] = useState<AppSetting | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    console.log('[USE-APP-SETTINGS] Hook initialized:', {
      settingKey,
      timestamp: new Date().toISOString()
    });

    const fetchSettings = async () => {
      try {
        console.log('[USE-APP-SETTINGS] Starting fetch for:', settingKey || 'all settings');
        setLoading(true);
        setError(null);
        
        let query = supabase.from('app_settings').select('*');
        
        if (settingKey) {
          query = query.eq('key', settingKey);
          console.log('[USE-APP-SETTINGS] Filtering by key:', settingKey);
        }
        
        const { data, error } = await query;
        
        console.log('[USE-APP-SETTINGS] Query result:', {
          data: data?.length || 0,
          error: error?.message || null,
          settingKey
        });
        
        if (error) {
          console.error('[USE-APP-SETTINGS] Query error:', error);
          throw error;
        }
        
        if (data) {
          console.log('[USE-APP-SETTINGS] Settings loaded:', {
            count: data.length,
            keys: data.map(s => s.key),
            settingKey
          });
          
          setSettings(data);
          
          if (settingKey && data.length > 0) {
            console.log('[USE-APP-SETTINGS] Setting current setting:', {
              key: data[0].key,
              hasValue: !!data[0].value,
              valueLength: data[0].value?.length || 0
            });
            setCurrentSetting(data[0]);
          }
        }
      } catch (err: any) {
        console.error('[USE-APP-SETTINGS] Error fetching settings:', err);
        setError(err);
      } finally {
        setLoading(false);
        console.log('[USE-APP-SETTINGS] Fetch completed for:', settingKey || 'all settings');
      }
    };
    
    fetchSettings();
  }, [settingKey]);
  
  const updateSetting = async (key: string, value: string) => {
    try {
      console.log('[USE-APP-SETTINGS] Updating setting:', { key, valueLength: value.length });
      
      const { data, error } = await supabase
        .from('app_settings')
        .update({ value })
        .eq('key', key)
        .select();
        
      if (error) {
        console.error('[USE-APP-SETTINGS] Update error:', error);
        throw error;
      }
      
      if (data) {
        console.log('[USE-APP-SETTINGS] Setting updated successfully:', { key });
        // Update local state
        setSettings(prevSettings => 
          prevSettings.map(setting => 
            setting.key === key ? { ...setting, value } : setting
          )
        );
        
        if (settingKey === key) {
          setCurrentSetting(data[0]);
        }
        
        return data[0];
      }
    } catch (err: any) {
      console.error('[USE-APP-SETTINGS] Error updating setting:', err);
      toast.error(`Failed to update setting: ${err.message}`);
      throw err;
    }
  };
  
  return { 
    loading, 
    error, 
    settings, 
    currentSetting,
    updateSetting,
    getValue: (key: string) => settings.find(s => s.key === key)?.value || null
  };
};
